<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div class="weekly-payments-dashboard">
    <!-- Header -->
    <b-card class="mb-2 header-card">
      <div class="bubbles">
        <span
          v-for="n in 10"
          :key="n"
          class="bubble"
        />
      </div>
      <div class="d-flex justify-content-between align-items-center header-content">
        <div>
          <h4 class="mb-0">
            <feather-icon
              icon="TrendingUpIcon"
              size="24"
              class="mr-1"
            />
            Tableau de Bord - Paiements Hebdomadaires
          </h4>
          <small>Analyse des performances de la semaine en cours</small>
        </div>
        <div>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="fetchWeeklyPayments"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
            />
            Actualiser
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-2 text-muted">
        Chargement des données...
      </p>
    </div>

    <template v-else-if="summary">
      <!-- Summary Cards Row -->
      <b-row class="match-height">
        <!-- Total Revenue Card -->
        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card revenue-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Revenu Total
                </p>
                <h3 class="stat-value">
                  {{ formatCurrency(summary.current_week.total_revenue) }}
                </h3>
                <div :class="['stat-change', summary.growth.direction === 'up' ? 'text-success' : 'text-danger']">
                  <feather-icon
                    :icon="summary.growth.direction === 'up' ? 'TrendingUpIcon' : 'TrendingDownIcon'"
                    size="14"
                  />
                  <span>{{ Math.abs(summary.growth.percentage) }}% vs semaine précédente</span>
                </div>
              </div>
              <div class="stat-icon bg-light-primary">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Orders Card -->
        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card orders-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Commandes Traitées
                </p>
                <h3 class="stat-value">
                  {{ summary.current_week.total_orders }}
                </h3>
                <div class="stat-change text-muted">
                  <span>{{ summary.current_week.total_delivered }} livrées | {{ summary.current_week.total_returned }} retours</span>
                </div>
              </div>
              <div class="stat-icon bg-light-info">
                <feather-icon
                  icon="PackageIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Success Rate Card -->
        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card success-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Taux de Réussite
                </p>
                <h3 class="stat-value">
                  {{ summary.current_week.success_rate }}%
                </h3>
                <b-progress
                  :value="summary.current_week.success_rate"
                  max="100"
                  height="6px"
                  :variant="getSuccessVariant(summary.current_week.success_rate)"
                  class="mt-1"
                />
              </div>
              <div class="stat-icon bg-light-success">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Delivery Fees Card -->
        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card fees-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Frais de Livraison
                </p>
                <h3 class="stat-value">
                  {{ formatCurrency(summary.current_week.total_delivery_fees) }}
                </h3>
                <div class="stat-change text-muted">
                  <span>Semaine {{ formatDateRange(summary.current_week.start_date, summary.current_week.end_date) }}</span>
                </div>
              </div>
              <div class="stat-icon bg-light-warning">
                <feather-icon
                  icon="TruckIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Charts Row -->
      <b-row class="match-height">
        <!-- Daily Breakdown Chart -->
        <b-col
          lg="8"
          md="12"
        >
          <b-card>
            <b-card-header class="pb-0">
              <h5>
                <feather-icon
                  icon="BarChart2Icon"
                  size="18"
                  class="mr-50"
                />
                Évolution Journalière
              </h5>
            </b-card-header>
            <b-card-body>
              <div class="daily-chart">
                <div
                  v-for="day in dailyBreakdown"
                  :key="day.date"
                  class="day-bar-container"
                >
                  <div class="day-info">
                    <span class="day-name">{{ getDayShortName(day.day_name) }}</span>
                    <span class="day-date">{{ formatShortDate(day.date) }}</span>
                  </div>
                  <div class="bar-wrapper">
                    <div
                      class="bar revenue-bar"
                      :style="{ width: getBarWidth(day.total_ttc) + '%' }"
                    >
                      <span
                        v-if="day.total_ttc > 0"
                        class="bar-value"
                      >{{ formatCurrency(day.total_ttc) }}</span>
                    </div>
                  </div>
                  <div class="day-stats">
                    <b-badge
                      variant="light-primary"
                      pill
                    >
                      {{ day.order_count }} cmd
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Payment Status -->
        <b-col
          lg="4"
          md="12"
        >
          <b-card>
            <b-card-header class="pb-0">
              <h5>
                <feather-icon
                  icon="CreditCardIcon"
                  size="18"
                  class="mr-50"
                />
                Statut des Paiements
              </h5>
            </b-card-header>
            <b-card-body>
              <div class="payment-status-item validated mb-2">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="status-label">
                    <feather-icon
                      icon="CheckIcon"
                      size="14"
                      class="text-success mr-50"
                    />
                    Validés
                  </span>
                  <b-badge variant="success">
                    {{ summary.payments.validated.count }}
                  </b-badge>
                </div>
                <div class="status-details">
                  <small class="text-muted d-block">Livraisons: {{ formatCurrency(summary.payments.validated.total_delivered) }}</small>
                  <small class="text-muted d-block">Retours: {{ formatCurrency(summary.payments.validated.total_return) }}</small>
                </div>
              </div>
              <div class="payment-status-item pending">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="status-label">
                    <feather-icon
                      icon="ClockIcon"
                      size="14"
                      class="text-warning mr-50"
                    />
                    En Attente
                  </span>
                  <b-badge variant="warning">
                    {{ summary.payments.pending.count }}
                  </b-badge>
                </div>
                <div class="status-details">
                  <small class="text-muted d-block">Total: {{ formatCurrency(summary.payments.pending.total_delivered) }}</small>
                </div>
              </div>
              <hr class="my-2">
              <div class="comparison-section">
                <h6 class="text-muted mb-1">
                  Semaine Précédente
                </h6>
                <div class="d-flex justify-content-between">
                  <span>Commandes</span>
                  <strong>{{ summary.previous_week.total_orders }}</strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span>Revenu</span>
                  <strong>{{ formatCurrency(summary.previous_week.total_revenue) }}</strong>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Repositories & Suppliers Row -->
      <b-row class="match-height">
        <!-- Repositories Performance -->
        <b-col
          lg="6"
          md="12"
        >
          <b-card>
            <b-card-header class="pb-0">
              <h5>
                <feather-icon
                  icon="HomeIcon"
                  size="18"
                  class="mr-50"
                />
                Performance par Dépôt
              </h5>
            </b-card-header>
            <b-card-body>
              <div
                v-if="repositories.length === 0"
                class="text-center text-muted py-3"
              >
                Aucune donnée disponible
              </div>
              <div
                v-for="repo in repositories"
                v-else
                :key="repo.id"
                class="repo-item mb-2 p-2 border rounded"
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <strong>{{ repo.name }}</strong>
                  <b-badge :variant="getSuccessVariant(repo.success_rate)">
                    {{ repo.success_rate }}%
                  </b-badge>
                </div>
                <div class="d-flex justify-content-between text-muted small">
                  <span>{{ repo.week_orders }} commandes</span>
                  <span>{{ formatCurrency(repo.week_revenue) }}</span>
                </div>
                <b-progress
                  class="mt-1"
                  height="4px"
                >
                  <b-progress-bar
                    :value="repo.delivered_count"
                    :max="repo.week_orders"
                    variant="success"
                  />
                  <b-progress-bar
                    :value="repo.returned_count"
                    :max="repo.week_orders"
                    variant="danger"
                  />
                </b-progress>
                <div class="d-flex justify-content-between mt-1">
                  <small class="text-success">{{ repo.delivered_count }} livrées</small>
                  <small class="text-danger">{{ repo.returned_count }} retours</small>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Top Suppliers -->
        <b-col
          lg="6"
          md="12"
        >
          <b-card>
            <b-card-header class="pb-0">
              <h5>
                <feather-icon
                  icon="UsersIcon"
                  size="18"
                  class="mr-50"
                />
                Top Fournisseurs
              </h5>
            </b-card-header>
            <b-card-body>
              <div
                v-if="topSuppliers.length === 0"
                class="text-center text-muted py-3"
              >
                Aucune donnée disponible
              </div>
              <b-table
                v-else
                :items="topSuppliers"
                :fields="supplierFields"
                responsive
                small
                hover
                class="mb-0"
              >
                <template #cell(name)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar
                      size="32"
                      :text="getInitials(data.item.name)"
                      variant="light-primary"
                      class="mr-1"
                    />
                    <span>{{ data.item.name }}</span>
                  </div>
                </template>
                <template #cell(week_revenue)="data">
                  <strong class="text-primary">{{ formatCurrency(data.item.week_revenue) }}</strong>
                </template>
                <template #cell(success_rate)="data">
                  <b-badge :variant="getSuccessVariant(data.item.success_rate)">
                    {{ data.item.success_rate }}%
                  </b-badge>
                </template>
                <template #cell(stats)="data">
                  <small class="text-success">{{ data.item.week_delivered }}</small>
                  <small class="text-muted">/</small>
                  <small class="text-danger">{{ data.item.week_returned }}</small>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <!-- Empty State -->
    <div
      v-else-if="!isLoading"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-2"
      />
      <h5 class="text-muted">
        Aucune donnée disponible
      </h5>
      <p class="text-muted">
        Les données de paiements hebdomadaires apparaîtront ici.
      </p>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BSpinner,
  BCol,
  BRow,
  BButton,
  BProgress,
  BProgressBar,
  BBadge,
  BTable,
  BAvatar,
} from 'bootstrap-vue'

import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'WeeklyPaymentsDashboard',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BSpinner,
    BCol,
    BRow,
    BButton,
    BProgress,
    BProgressBar,
    BBadge,
    BTable,
    BAvatar,
  },
  data() {
    return {
      isLoading: false,
      user: {},
      summary: null,
      dailyBreakdown: [],
      repositories: [],
      topSuppliers: [],
      maxDailyRevenue: 0,
      supplierFields: [
        { key: 'name', label: 'Fournisseur' },
        { key: 'week_orders', label: 'Cmd' },
        { key: 'week_revenue', label: 'Revenu' },
        { key: 'success_rate', label: 'Taux' },
        { key: 'stats', label: 'L/R' },
      ],
    }
  },
  created() {
    this.user = storeAuth.state.user
    if (this.user.role === 'admin' || this.user.role === 'superadmin') {
      this.fetchWeeklyPayments()
    }
  },
  methods: {
    async fetchWeeklyPayments() {
      try {
        this.isLoading = true
        const response = await axios.get('/api/kpi/weekly-payments/')

        this.summary = response.data.summary || null
        this.dailyBreakdown = response.data.daily_breakdown || []
        this.repositories = response.data.repositories || []
        this.topSuppliers = response.data.top_suppliers || []

        // Calculate max for bar chart scaling
        this.maxDailyRevenue = Math.max(...this.dailyBreakdown.map(d => d.total_ttc), 1)

        this.isLoading = false
      } catch (error) {
        console.error('Error fetching weekly payments:', error)
        this.isLoading = false
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.000 TND'
      return `${Number(value).toFixed(3)} TND`
    },
    formatDateRange(start, end) {
      if (!start || !end) return ''
      const startDate = new Date(start)
      const endDate = new Date(end)
      const options = { day: '2-digit', month: '2-digit' }
      return `${startDate.toLocaleDateString('fr-FR', options)} - ${endDate.toLocaleDateString('fr-FR', options)}`
    },
    formatShortDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    },
    getDayShortName(dayName) {
      const days = {
        Monday: 'Lun',
        Tuesday: 'Mar',
        Wednesday: 'Mer',
        Thursday: 'Jeu',
        Friday: 'Ven',
        Saturday: 'Sam',
        Sunday: 'Dim',
      }
      return days[dayName] || dayName
    },
    getBarWidth(value) {
      if (this.maxDailyRevenue === 0) return 0
      return Math.max((value / this.maxDailyRevenue) * 100, 0)
    },
    getSuccessVariant(rate) {
      if (rate >= 80) return 'success'
      if (rate >= 60) return 'warning'
      return 'danger'
    },
    getInitials(name) {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
        .slice(0, 2)
    },
  },
}
</script>

<style lang="scss" scoped>
@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes rise {
  0% {
    bottom: -100px;
    transform: translateX(0) scale(0.5);
    opacity: 0;
  }
  10% {
    opacity: 0.8;
  }
  50% {
    transform: translateX(100px) scale(1);
    opacity: 0.5;
  }
  100% {
    bottom: 120%;
    transform: translateX(-50px) scale(0.3);
    opacity: 0;
  }
}

.weekly-payments-dashboard {
  .header-card {
    background: linear-gradient(-45deg, #0A2634, #0d3040, #1a4a5e, #0A2634, #0A2634);
    background-size: 400% 400%;
    animation: gradientShift 8s ease infinite;
    color: white;
    position: relative;
    overflow: hidden;
    border: none;
    box-shadow: 0 10px 40px rgba(10, 38, 52, 0.3);

    .bubbles {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      overflow: hidden;

      .bubble {
        position: absolute;
        bottom: -100px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 50%;
        animation: rise 8s infinite ease-in;

        &:nth-child(1) {
          width: 40px;
          height: 40px;
          left: 10%;
          animation-duration: 8s;
          animation-delay: 0s;
        }

        &:nth-child(2) {
          width: 20px;
          height: 20px;
          left: 20%;
          animation-duration: 6s;
          animation-delay: 1s;
        }

        &:nth-child(3) {
          width: 50px;
          height: 50px;
          left: 35%;
          animation-duration: 10s;
          animation-delay: 2s;
        }

        &:nth-child(4) {
          width: 80px;
          height: 80px;
          left: 50%;
          animation-duration: 12s;
          animation-delay: 0s;
        }

        &:nth-child(5) {
          width: 35px;
          height: 35px;
          left: 55%;
          animation-duration: 7s;
          animation-delay: 3s;
        }

        &:nth-child(6) {
          width: 45px;
          height: 45px;
          left: 65%;
          animation-duration: 9s;
          animation-delay: 1.5s;
        }

        &:nth-child(7) {
          width: 25px;
          height: 25px;
          left: 75%;
          animation-duration: 6s;
          animation-delay: 2.5s;
        }

        &:nth-child(8) {
          width: 60px;
          height: 60px;
          left: 80%;
          animation-duration: 11s;
          animation-delay: 0.5s;
        }

        &:nth-child(9) {
          width: 15px;
          height: 15px;
          left: 90%;
          animation-duration: 5s;
          animation-delay: 4s;
        }

        &:nth-child(10) {
          width: 30px;
          height: 30px;
          left: 5%;
          animation-duration: 8s;
          animation-delay: 3.5s;
        }
      }
    }

    .header-content {
      position: relative;
      z-index: 1;
    }

    h4, small {
      color: white !important;
      position: relative;
      z-index: 1;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .btn-outline-primary {
      color: white;
      border-color: rgba(255, 255, 255, 0.8);
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      position: relative;
      z-index: 1;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
        border-color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
      }
    }
  }

  .stat-card {
    border: none;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);

    .stat-label {
      font-size: 0.875rem;
      color: #6e6b7b;
      margin-bottom: 0.5rem;
    }

    .stat-value {
      font-size: 1.75rem;
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.25rem;
    }

    .stat-change {
      font-size: 0.75rem;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .daily-chart {
    .day-bar-container {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      gap: 12px;

      .day-info {
        width: 60px;
        text-align: right;

        .day-name {
          display: block;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .day-date {
          display: block;
          font-size: 0.75rem;
          color: #b9b9c3;
        }
      }

      .bar-wrapper {
        flex: 1;
        background: #f3f2f7;
        border-radius: 4px;
        height: 28px;
        overflow: hidden;

        .bar {
          height: 100%;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-right: 8px;
          transition: width 0.3s ease;

          &.revenue-bar {
            background: linear-gradient(90deg, #7367f0, #9e95f5);
          }

          .bar-value {
            color: white;
            font-size: 0.75rem;
            font-weight: 500;
          }
        }
      }

      .day-stats {
        width: 60px;
      }
    }
  }

  .payment-status-item {
    padding: 12px;
    border-radius: 8px;

    &.validated {
      background: rgba(40, 199, 111, 0.1);
    }

    &.pending {
      background: rgba(255, 159, 67, 0.1);
    }

    .status-label {
      font-weight: 500;
    }
  }

  .repo-item {
    transition: all 0.2s ease;

    &:hover {
      background: #f8f8f8;
    }
  }

  .bg-light-primary {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  .bg-light-success {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }

  .bg-light-info {
    background: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
  }

  .bg-light-warning {
    background: rgba(255, 159, 67, 0.12);
    color: #ff9f43;
  }
}
</style>
